import { useState, useEffect } from 'react';

import type { OnboardingEditorState } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useCheckIfOnboardingEditorTemplate } from '@confluence/onboarding-helpers/entry-points/hooks/useCheckIfOnboardingEditorTemplate';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useIncludeEditorTutorialTemplate } from '@confluence/template-list/entry-points/hooks/useIncludeEditorTutorialTemplate';
import { useSessionData } from '@confluence/session-data';

type VersionType = { number?: number };

const DELAY_TIMEOUT = 3000;

export interface UseOnboardingBannerLogicProps {
	contentId: string;
	version?: number | VersionType;
	isShowingOfflineBanner: boolean;
	spaceKey: string;
	setIsOnboardingBannerDataLoaded: (value: boolean) => void;
}

export interface UseOnboardingBannerLogic {
	isVisible: boolean;
	setIsVisible: (value: boolean) => void;
	isLandInEditorBannerShown: boolean;
	showingOfflineBanner: boolean;
	onboardingStateData: any;
	onboardingStateLoading: boolean;
	onboardingStateError: any;
	deserializedData: OnboardingEditorState | undefined;
	editorOnboardingEligiblity: boolean;
	wasDraftBannerSeen: boolean;
	isLandInEditorBannerVisible: boolean;
	isTemplate: boolean;
	isOnboardingTemplate: boolean;
	labelsData: any;
	labelsLoading: boolean;
	labelsError: any;
	shouldIncludeEditorTutorialTemplate: boolean;
	hasQueryErrors: boolean;
}

export const useOnboardingBannerLogic = ({
	contentId,
	version,
	isShowingOfflineBanner,
	spaceKey,
	setIsOnboardingBannerDataLoaded,
}: UseOnboardingBannerLogicProps): UseOnboardingBannerLogic => {
	const { locale } = useSessionData();
	const [isVisible, setIsVisible] = useState(false);
	const [isLandInEditorBannerShown, setIsLandInEditorBannerShown] = useState(false);
	const [showingOfflineBanner, setShowingOfflineBanner] = useState<boolean>(false);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values(EDITOR_ONBOARDING_STATE_KEYS),
		(version as number) !== 1 && (version as VersionType)?.number !== 1,
	);
	const deserializedData = deserializeState(onboardingStateData);
	const { editorOnboardingEligiblity, wasDraftBannerSeen, isLandInEditorBannerVisible } =
		deserializedData;

	// Only check the labels, if they are eligible for the tutorial experiment to begin with.
	const skip =
		!onboardingStateData ||
		onboardingStateLoading ||
		Boolean(onboardingStateError) ||
		/* Check to see if they are a new user */ !editorOnboardingEligiblity ||
		isLandInEditorBannerVisible; //Skip if user is eligible for Land In Editor Banner

	const { isTemplate, isOnboardingTemplate, labelsData, labelsLoading, labelsError } =
		useCheckIfOnboardingEditorTemplate({
			contentId,
			skip,
		});

	const { shouldIncludeEditorTutorialTemplate, hasQueryErrors } =
		useIncludeEditorTutorialTemplate();

	// Connection banner height has a delay to appear, adjust the draft banner height to reflect this delay
	useEffect(() => {
		const delayTimeout: ReturnType<typeof setTimeout> = setTimeout(
			() => setShowingOfflineBanner(isShowingOfflineBanner),
			DELAY_TIMEOUT,
		);

		return () => clearTimeout(delayTimeout);
	}, [isShowingOfflineBanner]);

	useEffect(() => {
		if (labelsData && !labelsLoading) {
			// the original onboarding template should not have the draft banner
			if (isOnboardingTemplate || !shouldIncludeEditorTutorialTemplate) {
				setIsOnboardingBannerDataLoaded(true);
				return;
			}

			if (!wasDraftBannerSeen) {
				setIsVisible(true);
			}

			// land in editor banner has no labelsData
		} else if (isLandInEditorBannerVisible) {
			setIsVisible(true);
			setIsLandInEditorBannerShown(true);
		}
	}, [
		isOnboardingTemplate,
		isTemplate,
		labelsData,
		labelsLoading,
		locale,
		shouldIncludeEditorTutorialTemplate,
		setIsOnboardingBannerDataLoaded,
		spaceKey,
		wasDraftBannerSeen,
		isLandInEditorBannerVisible,
	]);

	return {
		isVisible,
		setIsVisible,
		isLandInEditorBannerShown,
		showingOfflineBanner,
		onboardingStateData,
		onboardingStateLoading,
		onboardingStateError,
		deserializedData,
		editorOnboardingEligiblity,
		wasDraftBannerSeen,
		isLandInEditorBannerVisible,
		isTemplate,
		isOnboardingTemplate,
		labelsData,
		labelsLoading,
		labelsError,
		shouldIncludeEditorTutorialTemplate,
		hasQueryErrors,
	};
};
